import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import { 
  getAllWarehouses, 
  getAllWarehousesIncludingInactive,
  createWarehouse, 
  updateWarehouse, 
  deactivateWarehouse,
  activateWarehouse,
} from '../services/api';
import LoadingAnimation from './LoadingAnimation';
import FullScreenLayout from './layouts/FullScreenLayout';
import WarehouseIcon from '@mui/icons-material/Warehouse';

const Warehouses = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [newWarehouse, setNewWarehouse] = useState({
    name: '',
    code: '',
    total_capacity: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMenuWarehouse, setSelectedMenuWarehouse] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, [showInactive]);

  const fetchWarehouses = async () => {
    try {
      const data = showInactive 
        ? await getAllWarehousesIncludingInactive()
        : await getAllWarehouses();
      setWarehouses(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch warehouses',
        severity: 'error',
      });
    }
  };

  const validateWarehouseInput = async () => {
    const errors = {};
    setIsValidating(true);

    // Basic validation
    if (!newWarehouse.name?.trim()) {
      errors.name = 'Warehouse name is required';
    } else if (newWarehouse.name.length < 3) {
      errors.name = 'Warehouse name must be at least 3 characters';
    }

    if (newWarehouse.code?.trim() && newWarehouse.code.length < 3) {
      errors.code = 'Warehouse code must be at least 3 characters';
    }

    if (newWarehouse.total_capacity && (isNaN(newWarehouse.total_capacity) || Number(newWarehouse.total_capacity) <= 0)) {
      errors.total_capacity = 'Total capacity must be a positive number';
    }

    // Check for duplicate name/code only if there are no basic validation errors
    if (Object.keys(errors).length === 0) {
      try {
        const existingWarehouses = await getAllWarehousesIncludingInactive();
        const nameExists = existingWarehouses.some(w => 
          w.name.toLowerCase() === newWarehouse.name.toLowerCase() &&
          w.warehouse_id !== newWarehouse.warehouse_id
        );
        
        if (nameExists) {
          errors.name = 'A warehouse with this name already exists';
        }

        if (newWarehouse.code) {
          const codeExists = existingWarehouses.some(w => 
            w.code.toLowerCase() === newWarehouse.code.toLowerCase() &&
            w.warehouse_id !== newWarehouse.warehouse_id
          );
          
          if (codeExists) {
            errors.code = 'A warehouse with this code already exists';
          }
        }
      } catch (error) {
        console.error('Error checking for duplicates:', error);
        errors.general = 'Error validating warehouse details';
      }
    }

    setValidationErrors(errors);
    setIsValidating(false);
    return Object.keys(errors).length === 0;
  };

  const handleCreateWarehouse = async () => {
    if (isValidating) return;

    const isValid = await validateWarehouseInput();
    if (!isValid) return;

    try {
      setIsLoading(true);
      const response = await createWarehouse(newWarehouse);
      
      setOpenDialog(false);
      setNewWarehouse({ name: '', code: '', total_capacity: '' });
      await fetchWarehouses();
      setShowSuccessDialog(true);
      setSnackbar({
        open: true,
        message: 'Warehouse created successfully',
        severity: 'success',
      });

      // Navigate to the new warehouse details page
      if (response?.warehouse_id) {
        navigate(`/warehouses/${response.warehouse_id}`);
      }
    } catch (error) {
      console.error('Error creating warehouse:', error);
      let errorMessage = 'Failed to create warehouse';
      
      if (error.response?.data?.error) {
        if (error.response.data.error.includes('code already exists')) {
          setValidationErrors(prev => ({
            ...prev,
            code: 'This warehouse code already exists'
          }));
        } else if (error.response.data.error.includes('name already exists')) {
          setValidationErrors(prev => ({
            ...prev,
            name: 'This warehouse name already exists'
          }));
        } else {
          errorMessage = error.response.data.error;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWarehouseClick = (warehouse) => {
    if (warehouse.status === 'active') {
      navigate(`/warehouses/${warehouse.warehouse_id}`);
    }
  };

  const handleMenuOpen = (event, warehouse) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMenuWarehouse(warehouse);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMenuWarehouse(null);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    setSelectedWarehouse(selectedMenuWarehouse);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleDeactivate = async (event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to deactivate this warehouse?')) {
      handleMenuClose();
      return;
    }

    try {
      setIsLoading(true);
      await deactivateWarehouse(selectedMenuWarehouse.warehouse_id);
      await fetchWarehouses();
      setSnackbar({
        open: true,
        message: 'Warehouse deactivated successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to deactivate warehouse',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleActivate = async (event) => {
    event.stopPropagation();
    try {
      setIsLoading(true);
      await activateWarehouse(selectedMenuWarehouse.warehouse_id);
      await fetchWarehouses();
      setSnackbar({
        open: true,
        message: 'Warehouse activated successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to activate warehouse',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleEditWarehouse = async () => {
    try {
      setIsLoading(true);
      await updateWarehouse(selectedWarehouse.warehouse_id, {
        name: selectedWarehouse.name,
        total_capacity: selectedWarehouse.total_capacity
      });
      setOpenEditDialog(false);
      await fetchWarehouses();
      setSnackbar({
        open: true,
        message: 'Warehouse updated successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update warehouse',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FullScreenLayout
      header={
        <Paper 
          elevation={3} 
          sx={{ 
            backgroundColor: 'background.paper',
            p: 3,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <WarehouseIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4">Warehouses</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show Inactive"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                }}
              >
                Add Warehouse
              </Button>
            </Box>
          </Box>
        </Paper>
      }
      mainPanel={
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {warehouses.map((warehouse) => (
              <Grid item xs={12} sm={6} md={4} key={warehouse.warehouse_id}>
                <Card 
                  sx={{ 
                    cursor: warehouse.status === 'active' ? 'pointer' : 'default',
                    '&:hover': warehouse.status === 'active' ? { 
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.2s ease-in-out'
                    } : {},
                    position: 'relative',
                    opacity: warehouse.status === 'active' ? 1 : 0.7,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: warehouse.status === 'active' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px dashed rgba(0, 0, 0, 0.23)',
                    height: '100%',
                  }}
                  onClick={() => handleWarehouseClick(warehouse)}
                  elevation={warehouse.status === 'active' ? 2 : 0}
                >
                  <Box 
                    sx={{ 
                      height: '8px', 
                      backgroundColor: warehouse.status === 'active' ? 'primary.main' : 'grey.400',
                      width: '100%'
                    }} 
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        {warehouse.name}
                      </Typography>
                      <IconButton 
                        onClick={(e) => handleMenuOpen(e, warehouse)}
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          }
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                      <Typography color="textSecondary" variant="body2">Warehouse Code</Typography>
                      <Chip 
                        label={warehouse.code || 'N/A'} 
                        size="small"
                        sx={{ 
                          fontWeight: 'medium',
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        }}
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography color="textSecondary" variant="body2">Capacity</Typography>
                      <Box display="flex" alignItems="center">
                        <CircularProgress
                          variant="determinate"
                          value={Math.min((warehouse.current_total_inventory / warehouse.total_capacity) * 100, 100)}
                          size={24}
                          sx={{ 
                            mr: 1,
                            color: (warehouse.current_total_inventory / warehouse.total_capacity) > 0.8 
                              ? 'error.main' 
                              : (warehouse.current_total_inventory / warehouse.total_capacity) > 0.6 
                                ? 'warning.main' 
                                : 'success.main'
                          }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {warehouse.current_total_inventory} / {warehouse.total_capacity}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography color="textSecondary" variant="body2">Status</Typography>
                      <Chip 
                        label={warehouse.status} 
                        size="small"
                        color={warehouse.status === 'active' ? 'success' : 'default'}
                        sx={{ 
                          textTransform: 'capitalize',
                          fontWeight: 'medium',
                          px: 1,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: {
                borderRadius: 2,
                minWidth: 180,
                p: 0.5,
              }
            }}
          >
            <MenuItem onClick={handleEdit} sx={{ borderRadius: 1, py: 1 }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            {selectedMenuWarehouse?.status === 'active' ? (
              <MenuItem onClick={handleDeactivate} sx={{ borderRadius: 1, py: 1 }}>
                <ListItemIcon>
                  <VisibilityOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Deactivate</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleActivate} sx={{ borderRadius: 1, py: 1 }}>
                <ListItemIcon>
                  <RestoreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Activate</ListItemText>
              </MenuItem>
            )}
          </Menu>

          <Dialog 
            open={openDialog} 
            onClose={() => setOpenDialog(false)}
            PaperProps={{
              sx: { 
                borderRadius: 3,
                maxWidth: 500,
                width: '100%',
              }
            }}
          >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>Create New Warehouse</DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                Enter details for your new warehouse. Code and capacity are optional and will be auto-generated if not provided.
              </Typography>
              {validationErrors.general && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {validationErrors.general}
                </Alert>
              )}
              <TextField
                autoFocus
                margin="dense"
                label="Warehouse Name"
                fullWidth
                value={newWarehouse.name}
                onChange={(e) => {
                  setNewWarehouse({ ...newWarehouse, name: e.target.value });
                  setValidationErrors({ ...validationErrors, name: null });
                }}
                sx={{ mb: 3 }}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                disabled={isLoading || isValidating}
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
              <TextField
                margin="dense"
                label="Warehouse Code (Optional)"
                fullWidth
                value={newWarehouse.code}
                onChange={(e) => {
                  setNewWarehouse({ ...newWarehouse, code: e.target.value });
                  setValidationErrors({ ...validationErrors, code: null });
                }}
                helperText={validationErrors.code || "Will be auto-generated if not provided"}
                error={!!validationErrors.code}
                sx={{ mb: 3 }}
                disabled={isLoading || isValidating}
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
              <TextField
                margin="dense"
                label="Total Capacity (Optional)"
                type="number"
                fullWidth
                value={newWarehouse.total_capacity}
                onChange={(e) => {
                  setNewWarehouse({ ...newWarehouse, total_capacity: e.target.value });
                  setValidationErrors({ ...validationErrors, total_capacity: null });
                }}
                helperText={validationErrors.total_capacity || "Default is 10,000 if not provided"}
                error={!!validationErrors.total_capacity}
                disabled={isLoading || isValidating}
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setOpenDialog(false)}
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  borderRadius: 2,
                }}
                disabled={isLoading || isValidating}
              >
                Cancel
              </Button>
              <LoadingButton 
                onClick={handleCreateWarehouse} 
                variant="contained"
                loading={isLoading || isValidating}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                }}
              >
                Create Warehouse
              </LoadingButton>
            </DialogActions>
          </Dialog>

          <Dialog 
            open={openEditDialog} 
            onClose={() => setOpenEditDialog(false)}
            PaperProps={{
              sx: { 
                borderRadius: 3,
                maxWidth: 500,
                width: '100%',
              }
            }}
          >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>Edit Warehouse</DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                Update warehouse details.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Warehouse Name"
                fullWidth
                value={selectedWarehouse?.name || ''}
                onChange={(e) => setSelectedWarehouse({ ...selectedWarehouse, name: e.target.value })}
                sx={{ mb: 3 }}
                required
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
              <TextField
                margin="dense"
                label="Total Capacity"
                type="number"
                fullWidth
                value={selectedWarehouse?.total_capacity || ''}
                onChange={(e) => setSelectedWarehouse({ ...selectedWarehouse, total_capacity: e.target.value })}
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setOpenEditDialog(false)}
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  borderRadius: 2,
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEditWarehouse} 
                variant="contained"
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>

          {isLoading && <LoadingAnimation />}

          <Dialog 
            open={showSuccessDialog} 
            onClose={() => setShowSuccessDialog(false)}
            PaperProps={{
              sx: { 
                borderRadius: 3,
                maxWidth: 500,
                width: '100%',
              }
            }}
          >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>Warehouse Created Successfully!</DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
              <Typography>What would you like to do next?</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setShowSuccessDialog(false)}
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  borderRadius: 2,
                }}
              >
                Stay Here
              </Button>
              <Button 
                onClick={() => {
                  setShowSuccessDialog(false);
                  setOpenDialog(true);
                }} 
                color="primary"
                sx={{ 
                  textTransform: 'none',
                  px: 3,
                  borderRadius: 2,
                }}
              >
                Create Another
              </Button>
              <Button 
                onClick={() => {
                  const newWarehouseId = warehouses[0].warehouse_id;
                  setShowSuccessDialog(false);
                  navigate(`/warehouses/${newWarehouseId}`);
                }} 
                variant="contained"
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                }}
              >
                Go to Warehouse Details
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert 
              severity={snackbar.severity} 
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ 
                borderRadius: 2,
                boxShadow: 3,
                width: '100%'
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      }
    />
  );
};

export default Warehouses; 